import { QrOverlay } from './QrOverlay.js';
import { useT } from '../i18n/LocaleProvider.js';
import type { MessageKey } from '../i18n/ko.js';

/**
 * 핸드오프가 성립하지 않았을 때의 안내.
 *
 * 두 경우가 여기로 온다.
 *
 * - `handoff: 'none'` — 서버가 어느 경로도 만들지 못했다 (`docs/08` §2: QR 표시를
 *   지원하지 않는 클라이언트 등).
 * - 트리거 호출 실패 — 타임아웃·네트워크·5xx (`tv2m.ts` 의 `TriggerError`).
 *
 * **폴백 QR 이 이 컴포넌트의 실체다.** TV2M 이 통하지 않아도 시청자에게는 살 방법이
 * 있어야 하고, MVP 에는 그것이 이미 있다 — BLOOM Server 의 간이 랜딩(`/p/{productId}`)과 추적
 * 4파라미터다(절대 규칙 8). `docs/09` T8 이 `qr.ts`·`qr.test.ts` 를 **지우지 말라**고
 * 적은 이유가 이 자리이고, 그 파일이 추적 파라미터를 지키는 유일한 자동 검사이기도 하다.
 *
 * **재생은 계속된다.** 실패했다고 영상을 멈추면 시청자가 채널을 떠나고, 그것이 이
 * 시스템이 막으려는 일 그대로다 (절대 규칙 7·14).
 */
export function HandoffErrorOverlay({
  reason,
  fallbackUrl,
}: {
  /** 화면에 보일 한 줄의 **사전 키**. `tv2m.ts` 의 실패 종류마다 다르다 */
  reason: MessageKey;
  /** BLOOM 랜딩 폴백. 상품 정보가 없으면 `null` 이고 그때는 안내만 띄운다 */
  fallbackUrl: string | null;
}) {
  const t = useT();

  if (fallbackUrl === null) {
    return (
      <div className="overlay overlay--handoff">
        <p className="overlay__handoff-title">{t('handoff.failed')}</p>
        <p className="overlay__handoff-hint">{t(reason)}</p>
      </div>
    );
  }

  // 폴백에서는 안내 문구를 QR 힌트로 대신한다 — 오버레이 둘을 겹쳐 띄우면 화면 하단
  // 1/3 규칙(`docs/06-frontend-spec.md`)을 넘고, 재생 화면을 가리기 시작한다.
  return <QrOverlay url={fallbackUrl} hint={t('handoff.failedQrHint', { reason: t(reason) })} />;
}
